import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "../../hooks/useAuth";
import {
  getMarketingActivitiesActive,
  submitMarketingActivity,
} from "../../store/slices/user";
import styles from "./ActivityTab.module.css";

export default function ActivityTab() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { marketingActivitiesActive } = useSelector((s) => s.user);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const userId = useMemo(() => {
    return (
      user?.id ||
      (typeof window !== "undefined" &&
        Number(localStorage.getItem("userId"))) ||
      null
    );
  }, [user]);

  useEffect(() => {
    if (userId) dispatch(getMarketingActivitiesActive(userId));
  }, [dispatch, userId]);

  if (!marketingActivitiesActive?.length) {
    return <div className={styles.empty}>ยังไม่มีกิจกรรมให้ทำ</div>;
  }

  if (selectedActivity) {
    return (
      <ActivityDetail
        activity={selectedActivity}
        onBack={() => setSelectedActivity(null)}
        userId={userId}
        dispatch={dispatch}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.activityGrid}>
        {marketingActivitiesActive.map((activity, index) => (
          <div
            key={activity.id}
            className={`${styles.card} ${
              activity.canSubmit === false ? styles.cardCompleted : ""
            }`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {activity.coverImage && (
              <div className={styles.coverWrapper}>
                <img
                  src={activity.coverImage}
                  alt={activity.title}
                  className={styles.cover}
                  loading="lazy"
                />
                {activity.canSubmit === false && (
                  <div className={styles.completedOverlay}>
                    <div className={styles.completedBadge}>
                      <span className={styles.checkIcon}>✓</span>
                      <span>เสร็จสิ้น</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={styles.content}>
              <div className={styles.header}>
                <h3 className={styles.title}>{activity.title}</h3>
                <div className={styles.rewardBadge}>
                  <span className={styles.rewardIcon}>🎁</span>
                  <span className={styles.rewardAmount}>
                    {activity.rewardAmount}
                  </span>
                  <span className={styles.rewardType}>
                    {activity.rewardType === "CASH" ? "THB" : "เพชร"}
                  </span>
                </div>
              </div>

              {activity.description && (
                <p className={styles.description}>
                  {activity.description.length > 80
                    ? `${activity.description.substring(0, 80)}...`
                    : activity.description}
                </p>
              )}

              <div className={styles.actions}>
                <button
                  className={styles.primaryBtn}
                  onClick={() => setSelectedActivity(activity)}
                >
                  <span className={styles.btnIcon}>👁️</span>
                  <span>ดูกิจกรรม</span>
                  <span className={styles.btnArrow}>→</span>
                </button>
                <div
                  className={`${styles.statusIndicator} ${
                    activity.canSubmit === false
                      ? styles.statusCompleted
                      : styles.statusPending
                  }`}
                >
                  <span className={styles.statusDot}></span>
                  <span className={styles.statusText}>
                    {activity.canSubmit === false ? "เสร็จสิ้น" : "ยังไม่ได้ทำ"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityDetail({ activity, onBack, userId, dispatch }) {
  const { marketingSubmitResult } = useSelector((s) => s.user);
  const [answers, setAnswers] = useState({});

  const handleChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = () => {
    if (!userId) return;
    const payload = {
      userId,
      answers: (activity.questions || []).map((q) => ({
        questionId: q.id,
        answer: answers[q.id] || "",
      })),
    };
    dispatch(submitMarketingActivity(activity.id, payload));
  };

  return (
    <div className={styles.wrapper}>
      <div className={`${styles.detailCard} ${styles.fadeIn}`}>
        <button className={styles.backBtn} onClick={onBack}>
          <span className={styles.backIcon}>←</span>
          <span>กลับ</span>
        </button>

        {activity.coverImage && (
          <div className={styles.detailCoverWrapper}>
            <img
              src={activity.coverImage}
              alt={activity.title}
              className={styles.detailCover}
              loading="lazy"
            />
          </div>
        )}

        <div className={styles.detailContent}>
          <div className={styles.detailHeader}>
            <h2 className={styles.detailTitle}>{activity.title}</h2>
            <div className={styles.detailMetaRow}>
              <div className={styles.frequencyBadge}>
                <span className={styles.frequencyIcon}>⏰</span>
                <span>
                  {activity.frequency === "ONCE"
                    ? "ทำได้ครั้งเดียว"
                    : activity.frequency === "DAILY"
                    ? "ทำได้วันละครั้ง"
                    : "ทำได้ตลอดเวลา"}
                </span>
              </div>
              <div className={styles.detailRewardBadge}>
                <span className={styles.rewardIcon}>🎁</span>
                <span className={styles.rewardValue}>
                  {activity.rewardAmount}{" "}
                  {activity.rewardType === "CASH" ? "THB" : "เพชร"}
                </span>
              </div>
            </div>
          </div>

          {activity.description && (
            <div className={styles.detailDesc}>
              <div className={styles.descIcon}>📝</div>
              <p>{activity.description}</p>
            </div>
          )}

          {activity.links?.length ? (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>🔗 ลิงก์ที่เกี่ยวข้อง</div>
              <div className={styles.links}>
                {activity.links.map((link) => (
                  <a
                    key={link.id || link.url}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.linkButton}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          ) : null}

          {activity.questions?.length ? (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>❓ คำถาม</div>
              <div className={styles.qaBox}>
                {activity.questions.map((q) => (
                  <div key={q.id}>
                    <label className={styles.questionLabel}>{q.question}</label>
                    <input
                      className={styles.input}
                      placeholder="พิมพ์คำตอบของคุณ..."
                      value={answers[q.id] || ""}
                      onChange={(e) => handleChange(q.id, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className={styles.actions}>
            <button
              className={styles.primaryBtn}
              onClick={handleSubmit}
              disabled={activity.canSubmit === false}
            >
              {activity.canSubmit === false ? "ทำแล้ว" : "ส่งคำตอบ"}
            </button>
            {activity.lastCompletedAt ? (
              <span className={styles.statusTag}>
                ทำล่าสุด: {new Date(activity.lastCompletedAt).toLocaleString()}
              </span>
            ) : null}
          </div>

          {marketingSubmitResult &&
          marketingSubmitResult.success !== undefined ? (
            <div
              className={`${styles.resultMessage} ${
                marketingSubmitResult.success ? styles.success : styles.error
              }`}
            >
              {marketingSubmitResult.message}
              {marketingSubmitResult.reward?.rewardAmount &&
                ` (+${marketingSubmitResult.reward.rewardAmount} ${
                  marketingSubmitResult.reward.rewardType === "CASH"
                    ? "THB"
                    : "เพชร"
                })`}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
